import { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SheetHeader from '../SheetHeader';

export const LEGAL_DOCUMENT_SLUGS = [
  'terms',
  'privacy',
  'aup',
  'refund',
  'cookie',
  'complaints',
];

export default function LegalDocumentsSheet({
  open,
  onClose,
  onSelect,
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setVisible(false);
        closeTimerRef.current = window.setTimeout(onClose, 200);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, open]);

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  const requestClose = () => {
    setVisible(false);
    closeTimerRef.current = window.setTimeout(onClose, 200);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end"
      data-testid="legal-documents-sheet"
    >
      <button
        type="button"
        aria-label={t('common.close')}
        className={[
          'absolute inset-0 bg-black/[0.64] transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
        onClick={requestClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-documents-sheet-title"
        className={[
          'relative z-10 w-full rounded-t-home-24 bg-popup-gradient px-6 pt-6',
          'transform-gpu transition-transform duration-200 ease-out',
          visible ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
        style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 24px)' }}
      >
        <SheetHeader
          title={t('profile.legalDocuments.title')}
          titleId="legal-documents-sheet-title"
          onClose={requestClose}
        />

        <div className="flex flex-col gap-2">
          {LEGAL_DOCUMENT_SLUGS.map((slug) => (
            <button
              key={slug}
              type="button"
              onClick={() => onSelect(slug)}
              className={[
                'flex min-h-12 w-full items-center gap-3 rounded-home-12',
                'border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-left',
                'font-sans text-plan text-text-primary transition-colors',
                'hover:bg-white/[0.1] active:bg-white/[0.12]',
              ].join(' ')}
              data-testid={`legal-document-option-${slug}`}
            >
              <span className="min-w-0 flex-1">
                {t(`profile.legalDocuments.items.${slug}`)}
              </span>
              <ChevronRight
                aria-hidden="true"
                className="shrink-0 text-text-secondary"
                size={20}
                strokeWidth={2}
              />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
